/**
 * Replay Page - View match replays with playback controls
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { replaysApi, ReplaySummary, FullReplay } from '../../api/replays';
import { formatLamports } from '../../utils/format';

export const ReplayPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();

  const [replays, setReplays] = useState<ReplaySummary[]>([]);
  const [selectedReplay, setSelectedReplay] = useState<FullReplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Playback state
  const [currentSnapshot, setCurrentSnapshot] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const playbackRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch replays list or specific replay
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (matchId) {
          const replay = await replaysApi.getReplay(matchId);
          setSelectedReplay(replay);
          setCurrentSnapshot(0);
        } else {
          const recentReplays = await replaysApi.getRecent(20);
          setReplays(recentReplays);
        }
        setError(null);
      } catch (err) {
        setError(matchId ? 'Replay not found' : 'Failed to load replays');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [matchId]);

  // Playback logic
  useEffect(() => {
    if (isPlaying && selectedReplay) {
      playbackRef.current = setInterval(() => {
        setCurrentSnapshot((prev) => {
          if (prev >= selectedReplay.snapshots.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1500 / playbackSpeed);
    }

    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, selectedReplay, playbackSpeed]);

  const handlePlay = useCallback(() => {
    if (selectedReplay && currentSnapshot >= selectedReplay.snapshots.length - 1) {
      setCurrentSnapshot(0);
    }
    setIsPlaying(true);
  }, [selectedReplay, currentSnapshot]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleSeek = useCallback((index: number) => {
    setCurrentSnapshot(index);
    setIsPlaying(false);
  }, []);

  const handleSpeedChange = useCallback(() => {
    setPlaybackSpeed((prev) => {
      if (prev === 1) return 2;
      if (prev === 2) return 0.5;
      return 1;
    });
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neon-500 border-t-transparent" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 p-4">
        <div className="mx-auto max-w-2xl text-center py-20">
          <div className="text-4xl mb-4">😕</div>
          <h1 className="text-xl font-semibold text-white mb-2">{error}</h1>
          <Link to="/replays" className="text-neon-300 hover:underline">
            Back to Replays
          </Link>
        </div>
      </div>
    );
  }

  // Replay viewer
  if (selectedReplay) {
    const snapshot = selectedReplay.snapshots[currentSnapshot];
    const players = snapshot ? Object.values(snapshot.players) : [];
    const player1 = players[0];
    const player2 = players[1];

    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 p-4">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Link to="/replays" className="flex items-center gap-2 text-white/70 hover:text-white">
              <span>← Back</span>
            </Link>
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/50">
                Match #{selectedReplay.matchId.slice(0, 8)}
              </span>
            </div>
          </div>

          {/* Match result banner */}
          <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {selectedReplay.players.map((p, i) => (
                  <span
                    key={p.wallet}
                    className={`font-semibold ${
                      p.wallet === selectedReplay.winner ? 'text-green-400' : 'text-white/70'
                    }`}
                  >
                    {p.username} {p.wallet === selectedReplay.winner && '👑'}
                    {i < selectedReplay.players.length - 1 && (
                      <span className="ml-2 text-white/30">vs</span>
                    )}
                  </span>
                ))}
              </div>
              <span className="rounded-full bg-neon-500/20 px-3 py-1 text-xs text-neon-300">
                {selectedReplay.wagerAmount > 0
                  ? formatLamports(
                      BigInt(selectedReplay.wagerAmount),
                      selectedReplay.currency === 'USDC' ? 'USDC' : 'SOL',
                    )
                  : 'Practice'}
              </span>
            </div>
          </div>

          {/* Players state */}
          {snapshot && (
            <div className="mb-6 grid gap-4 md:grid-cols-2">
              {/* Player 1 */}
              <motion.div
                key={`p1-${currentSnapshot}`}
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-semibold text-blue-300">
                    {player1?.wallet?.slice(0, 8) || 'Player 1'}
                  </span>
                  <div className="flex gap-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <span
                        key={i}
                        className={`text-lg ${i < (player1?.hearts || 0) ? 'text-red-500' : 'text-white/20'}`}
                      >
                        ♥
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-3xl font-bold text-white">{player1?.score || 0}</span>
                  <span className="ml-2 text-white/50">pts</span>
                </div>
              </motion.div>

              {/* Player 2 */}
              <motion.div
                key={`p2-${currentSnapshot}`}
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                className="rounded-xl border border-purple-500/30 bg-purple-500/10 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-semibold text-purple-300">
                    {player2?.wallet?.slice(0, 8) || 'Player 2'}
                  </span>
                  <div className="flex gap-1">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <span
                        key={i}
                        className={`text-lg ${i < (player2?.hearts || 0) ? 'text-red-500' : 'text-white/20'}`}
                      >
                        ♥
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-3xl font-bold text-white">{player2?.score || 0}</span>
                  <span className="ml-2 text-white/50">pts</span>
                </div>
              </motion.div>
            </div>
          )}

          {/* Action display */}
          {snapshot?.action && (
            <motion.div
              key={`action-${currentSnapshot}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-center"
            >
              {snapshot.action.type === 'play_hand' && (
                <div>
                  <span className="text-amber-300">
                    {snapshot.action.handType} for {snapshot.action.score} pts
                  </span>
                </div>
              )}
              {snapshot.action.type === 'buy_joker' && (
                <span className="text-amber-300">Bought joker: {snapshot.action.jokerId}</span>
              )}
              {snapshot.action.type === 'skip_shop' && (
                <span className="text-amber-300">Skipped shop</span>
              )}
              {snapshot.action.type === 'reroll' && (
                <span className="text-amber-300">Rerolled shop</span>
              )}
            </motion.div>
          )}

          {/* Playback controls */}
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            {/* Timeline */}
            <div className="mb-4">
              <div className="relative h-2 rounded-full bg-white/10">
                <motion.div
                  className="absolute left-0 top-0 h-full rounded-full bg-neon-500"
                  style={{
                    width: `${((currentSnapshot + 1) / selectedReplay.snapshots.length) * 100}%`,
                  }}
                />
                {/* Clickable timeline segments */}
                <div className="absolute inset-0 flex">
                  {selectedReplay.snapshots.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handleSeek(i)}
                      className="flex-1 h-full cursor-pointer"
                      title={`Turn ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-1 flex justify-between text-xs text-white/40">
                <span>Turn 1</span>
                <span>Turn {selectedReplay.snapshots.length}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4">
              {/* Previous */}
              <button
                onClick={() => handleSeek(Math.max(0, currentSnapshot - 1))}
                disabled={currentSnapshot === 0}
                className="rounded-lg bg-white/10 p-2 text-white/70 hover:bg-white/20 disabled:opacity-50"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              {/* Play/Pause */}
              <button
                onClick={isPlaying ? handlePause : handlePlay}
                className="rounded-lg bg-neon-500/20 p-3 text-neon-300 hover:bg-neon-500/30"
              >
                {isPlaying ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 9v6m4-6v6"
                    />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Next */}
              <button
                onClick={() =>
                  handleSeek(Math.min(selectedReplay.snapshots.length - 1, currentSnapshot + 1))
                }
                disabled={currentSnapshot >= selectedReplay.snapshots.length - 1}
                className="rounded-lg bg-white/10 p-2 text-white/70 hover:bg-white/20 disabled:opacity-50"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Speed */}
              <button
                onClick={handleSpeedChange}
                className="ml-4 rounded-lg bg-white/10 px-3 py-2 text-sm text-white/70 hover:bg-white/20"
              >
                {playbackSpeed}x
              </button>
            </div>

            {/* Current state */}
            <div className="mt-4 text-center text-sm text-white/50">
              Turn {currentSnapshot + 1} of {selectedReplay.snapshots.length}
              {snapshot && ` • ${snapshot.phase}`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Replays list
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 p-4">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-white">Match Replays</h1>
          <p className="text-white/50">Watch past matches and learn from them</p>
        </div>

        {/* No replays */}
        {replays.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
            <div className="mb-4 text-4xl">📼</div>
            <h2 className="mb-2 text-xl font-semibold text-white">No Replays Yet</h2>
            <p className="text-white/50">Completed matches will appear here</p>
          </div>
        )}

        {/* Replay list */}
        <AnimatePresence>
          <div className="space-y-4">
            {replays.map((replay) => (
              <motion.div
                key={replay.matchId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="rounded-xl border border-white/10 bg-white/5 p-4 hover:border-neon-500/30 transition"
              >
                <div className="flex items-center justify-between">
                  {/* Players */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {replay.players.map((p, i) => (
                        <span
                          key={p.wallet}
                          className={`font-medium ${
                            p.wallet === replay.winner ? 'text-green-400' : 'text-white/70'
                          }`}
                        >
                          {p.username}
                          {p.wallet === replay.winner && ' 👑'}
                          {i < replay.players.length - 1 && (
                            <span className="ml-2 text-white/30">vs</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Match info */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-white/50">{replay.snapshotCount} turns</span>
                    <span className="rounded-full bg-neon-500/20 px-3 py-1 text-xs text-neon-300">
                      {replay.wagerAmount > 0
                        ? formatLamports(
                            BigInt(replay.wagerAmount),
                            replay.currency === 'USDC' ? 'USDC' : 'SOL',
                          )
                        : 'Practice'}
                    </span>
                    <Link
                      to={`/replays/${replay.matchId}`}
                      className="rounded-lg bg-neon-500/20 px-4 py-2 text-sm font-medium text-neon-300 hover:bg-neon-500/30 transition"
                    >
                      Watch
                    </Link>
                  </div>
                </div>
                {replay.completedAt && (
                  <div className="mt-2 text-xs text-white/40">
                    {new Date(replay.completedAt).toLocaleDateString()}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </AnimatePresence>
      </div>
    </div>
  );
};
